import React from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Shield,
  UserCheck,
  MoreVertical,
  Edit,
  Trash2
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button/Button';
import { StatusBadge } from '../ui/table';
import { USER_ROLES } from '../../pages/users/UserReportsPage';

/**
 * UserCard Component
 * 
 * A detailed card view for displaying user information
 * with actions for viewing details, updating, and deleting users.
 */
export function UserCard({ 
  user, 
  onViewDetails, 
  onUpdateUser, 
  onDeleteUser,
  className = '' 
}) {
  const getRoleConfig = (role) => {
    const configs = {
      [USER_ROLES.ADMIN]: { variant: 'success', label: 'Admin', icon: Shield },
      [USER_ROLES.MODERATOR]: { variant: 'warning', label: 'Moderator', icon: UserCheck },
      [USER_ROLES.USER]: { variant: 'default', label: 'User', icon: User }
    };
    return configs[role] || { variant: 'default', label: role, icon: User };
  };

  const roleConfig = getRoleConfig(user.user_role);
  const RoleIcon = roleConfig.icon;

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  return (
    <Card className={`p-6 hover:shadow-lg transition-shadow ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{user.full_name}</h3>
            <p className="text-sm text-gray-500">@{user.username}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <StatusBadge status={roleConfig.label} variant={roleConfig.variant} />
          <div className="relative">
            <Button variant="ghost" size="sm" className="p-1">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* User Details */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Mail className="w-4 h-4" />
          <span>{user.email}</span>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Phone className="w-4 h-4" />
          <span>{user.phone_number}</span>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <MapPin className="w-4 h-4" />
          <span className="truncate">{user.address}</span>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>
            {new Date(user.date_of_birth).toLocaleDateString()} 
            <span className="text-gray-400 ml-1">
              (Age: {calculateAge(user.date_of_birth)})
            </span>
          </span>
        </div>

        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <RoleIcon className="w-4 h-4" />
          <span className="capitalize">{user.gender}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails?.(user)}
          className="flex-1 mr-2"
        >
          View Details
        </Button>
        
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onUpdateUser?.(user.id, user)}
            className="p-2"
            title="Edit User"
          >
            <Edit className="w-4 h-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDeleteUser?.(user.id)}
            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Delete User"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * UserCardCompact Component
 * 
 * A compact card view for displaying user information
 * in a more condensed format.
 */
export function UserCardCompact({ 
  user, 
  onViewDetails,
  className = '' 
}) {
  const getRoleConfig = (role) => {
    const configs = {
      [USER_ROLES.ADMIN]: { variant: 'success', label: 'Admin' },
      [USER_ROLES.MODERATOR]: { variant: 'warning', label: 'Moderator' },
      [USER_ROLES.USER]: { variant: 'default', label: 'User' }
    };
    return configs[role] || { variant: 'default', label: role };
  };

  const roleConfig = getRoleConfig(user.user_role);

  return (
    <div 
      className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${className}`}
      onClick={() => onViewDetails?.(user)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <h4 className="text-sm font-medium text-gray-900 truncate">
                {user.full_name}
              </h4>
              <StatusBadge status={roleConfig.label} variant={roleConfig.variant} size="sm" />
            </div>
            
            <div className="flex items-center space-x-4 mt-1">
              <span className="text-xs text-gray-500">@{user.username}</span>
              <span className="text-xs text-gray-500">{user.email}</span>
              <span className="text-xs text-gray-500 capitalize">{user.gender}</span>
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-xs text-gray-500">
            {user.address.split(',').pop()?.trim()}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            ID: #{String(user.id).padStart(4, '0')}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * UserCardSkeleton Component
 * 
 * Loading skeleton for user cards
 */
export function UserCardSkeleton({ className = '' }) {
  return (
    <Card className={`p-6 ${className}`}>
      <div className="animate-pulse">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
            <div>
              <div className="h-5 bg-gray-200 rounded w-32 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-20"></div>
            </div>
          </div>
          <div className="h-6 bg-gray-200 rounded w-16"></div>
        </div>

        {/* Details */}
        <div className="space-y-3 mb-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded flex-1"></div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="h-8 bg-gray-200 rounded flex-1 mr-2"></div>
          <div className="flex items-center space-x-1">
            <div className="w-8 h-8 bg-gray-200 rounded"></div>
            <div className="w-8 h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    </Card>
  );
}
